import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import AssemblyLoadingAnimation from 'molbio/bulk-assembly/AssemblyLoadingAnimation';
import {getConstructsTabBannersProps} from 'molbio/bulk-assembly/Banners';
import {
  BulkAssemblyMethod,
  PreviewConstruct,
  SequencePolymerInfo,
  AaSequencePolymerInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import ConstructPreviewCard from 'molbio/bulk-assembly/constructs-tab/ConstructPreviewCard';
import ConstructModal, {ConstructModalTab} from 'molbio/bulk-assembly/polymer-map-modals/ConstructModal';
import {useAssemblyCoordinator} from 'molbio/bulk-assembly/useAssemblyCoordinator';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {Header2} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import {LOADING} from 'util/Types';

const DEFAULT_PAGE_SIZE = 25;
const ALLOWED_PAGE_SIZES = [10, DEFAULT_PAGE_SIZE, 50, 100];

function searchConstructs(searchQuery: string, constructs: Array<PreviewConstruct>): Array<PreviewConstruct> {
  const trimmedQuery = searchQuery.trim().toLowerCase();
  if (trimmedQuery === '') {
    return constructs;
  } else {
    const searchResults = constructs.filter(
      ({name, fragmentNames}) =>
        name.toLowerCase().includes(trimmedQuery) ||
        fragmentNames.some((name) => name.toLowerCase().includes(trimmedQuery))
    );
    return searchResults;
  }
}

export function BulkAssemblyConstructsTabContent({
  assemblyId,
  bulkAssemblyMethod,
  canCreatePrimers,
  constructs,
  isArchived,
  isReadOnly,
  isLoading,
  canUnarchive,
  getConstructPolymer,
}: {
  assemblyId: string;
  bulkAssemblyMethod: BulkAssemblyMethod;
  canCreatePrimers: boolean;
  constructs: Array<PreviewConstruct>;
  isArchived: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
  canUnarchive: boolean;
  getConstructPolymer: (cosntructCid: string) => SequencePolymerInfo | AaSequencePolymerInfo | null;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const constructsToShow = searchConstructs(searchQuery, constructs).slice(offset, offset + pageSize);

  const bannerMessages = getConstructsTabBannersProps({
    numConstructsWithErrors: constructs.filter((construct) => construct.hasErrors).length,
    numConstructsWithWarnings: constructs.filter((construct) => construct.hasWarnings).length,
    isArchived,
    assemblyId,
    canUnarchive,
  });

  return (
    <>
      <div
        className={css(styles.tabContent)}
        {...getTestProps({component: 'BulkAssemblyConstructsTab', element: 'root'})}
      >
        <Header2>Constructs</Header2>

        {bannerMessages.length > 0 && (
          <div className={css(styles.bannerArea)}>
            <InlineBannerStack messages={bannerMessages} />
          </div>
        )}
        <PlainSearchInput
          className={css(styles.searchInput)}
          clearType={ClearType.X}
          onChange={(e) => {
            setSearchQuery(e.target.value ?? '');
            setOffset(0);
          }}
          onEscape={() => {
            setSearchQuery('');
            setOffset(0);
          }}
          placeholder="Search constructs by name"
          value={searchQuery}
        />
        <div className={css(styles.pagerSection)}>
          <ResultsPager
            allowFastForwardAndBackward={true}
            isLoading={false}
            setOffset={setOffset}
            pagination={{limit: pageSize, offset, total: constructs.length}}
          />
          <ListingSettingsDropdown
            pageSizeConfig={{
              availablePageSizes: ALLOWED_PAGE_SIZES,
              pageSize,
              onSetPageSize: (pageSize: number) => {
                setOffset(0);
                setPageSize(pageSize);
              },
            }}
          />
        </div>
        {isLoading && (
          <>
            <div className={css(styles.animationWrapper)}>
              <AssemblyLoadingAnimation />
            </div>
            <div className={css(styles.loadingTextWrapper)}>
              This assembly is still validating. Results may not be available yet.
            </div>
          </>
        )}
        <div className={css(styles.cardsSection)}>
          {constructsToShow.map((construct) => (
            <ConstructPreviewCard
              key={construct.cid}
              construct={construct}
              canCreatePrimers={canCreatePrimers}
              openConstructModal={async (initialTab: ConstructModalTab) => {
                await legacyRunModal(ConstructModal, {
                  assemblyId,
                  bulkAssemblyMethod,
                  canCreatePrimers,
                  // TODO: defer creating Backbone models until they are viewed
                  constructData: constructsToShow.map((previewConstruct) => ({
                    ...previewConstruct,
                    polymer: getConstructPolymer(previewConstruct.cid),
                  })),
                  initialConstructCid: construct.cid,
                  isReadOnly,
                  initialTab,
                });
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

interface BulkAssemblyConstructsTabProps {
  assemblyId: string;
}

export default function BulkAssemblyConstructsTab({assemblyId}: BulkAssemblyConstructsTabProps): JSX.Element {
  const assemblyCoordinator = useAssemblyCoordinator(assemblyId);
  if (assemblyCoordinator == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <BulkAssemblyConstructsTabContent
      assemblyId={assemblyId}
      bulkAssemblyMethod={assemblyCoordinator.assemblySelector.getMethod().type}
      isArchived={assemblyCoordinator.isArchived}
      isReadOnly={assemblyCoordinator.isReadOnly()}
      isLoading={assemblyCoordinator.validationResults.assemblyResult === LOADING}
      canUnarchive={Boolean(assemblyCoordinator.permissions.unarchivable)}
      constructs={assemblyCoordinator.getPreviewConstructs()}
      canCreatePrimers={assemblyCoordinator.canCreatePrimers()}
      getConstructPolymer={(constructCid) =>
        assemblyCoordinator.maybeCreatePreviewConstructPolymer(constructCid)
      }
    />
  );
}

const styles = StyleSheet.create({
  tabContent: {
    padding: '10px 16px',
  },
  bannerArea: {
    marginTop: 30,
    marginBottom: 30,
  },
  searchInput: {
    marginTop: 16,
  },
  cardsSection: {
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(266px, 1fr))',
  },
  pagerSection: {
    display: 'flex',
    marginTop: 8,
    marginBottom: 16,
  },
  animationWrapper: {
    display: 'flex',
    margin: '20px',
  },
  loadingTextWrapper: {
    textAlign: 'center',
  },
});
